<template>
  <b-overlay :show="loading" :opacity="0.6">
    <div class="self-profile-view">
      <!-- Hero Header Section -->
      <div class="hero-header">
        <div class="hero-pattern" />
        <div class="container-fluid">
          <b-row class="align-items-center py-4">
            <b-col cols="12" md="8" class="text-center text-md-left">
              <div class="d-flex flex-column flex-md-row align-items-center">
                <div class="avatar-wrapper mb-3 mb-md-0 mr-md-4">
                  <b-avatar
                    :src="avatarPath"
                    :text="avatarText(fullName)"
                    variant="white"
                    size="120"
                    class="profile-avatar"
                  />
                </div>
                <div class="profile-header-info">
                  <h1 class="profile-name mb-2">{{ fullName }}</h1>
                  <p v-if="roleLine" class="profile-title mb-2">{{ roleLine }}</p>
                  <div class="profile-meta">
                    <span v-if="locationSummary" class="meta-item">
                      <feather-icon icon="MapPinIcon" size="14" />
                      {{ locationSummary }}
                    </span>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" md="4" class="text-center text-md-right mt-3 mt-md-0">
              <b-link :to="{ name: 'auth-update-profile-account' }">
                <b-button variant="light" class="action-btn">
                  <feather-icon icon="SettingsIcon" size="16" />
                  <span>Account Settings</span>
                </b-button>
              </b-link>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="container-fluid mt-4 pb-5">
        <b-row>
          <!-- Sidebar Info Cards -->
          <b-col cols="12" lg="4" class="mb-4">
            <div class="info-card">
              <h5 class="card-header-title mb-4">
                <feather-icon icon="UserIcon" size="18" />
                Personal Information
              </h5>

              <div class="info-item">
                <span class="info-label">Date of Birth</span>
                <span class="info-value">{{ member_profile.date_of_birth || '-' }}</span>
              </div>

              <div class="info-item">
                <span class="info-label">Gender</span>
                <span class="info-value">{{ genderLabel || '-' }}</span>
              </div>

              <div class="info-item">
                <span class="info-label">Phone</span>
                <span class="info-value">
                  {{ phone || '-' }}
                  <b-badge v-if="phone" :variant="phoneVerified ? 'light-success' : 'light-danger'" class="ml-1">
                    {{ phoneVerified ? 'Verified' : 'Pending' }}
                  </b-badge>
                </span>
              </div>

              <div class="info-item">
                <span class="info-label">Email</span>
                <span class="info-value">
                  {{ email || '-' }}
                  <b-badge v-if="email" :variant="emailVerified ? 'light-success' : 'light-danger'" class="ml-1">
                    {{ emailVerified ? 'Verified' : 'Pending' }}
                  </b-badge>
                </span>
              </div>
            </div>

            <div class="info-card mt-4">
              <h5 class="card-header-title mb-4">
                <feather-icon icon="MapIcon" size="18" />
                Location Details
              </h5>

              <div class="info-item">
                <span class="info-label">Country</span>
                <span class="info-value">{{ member_profile.country || '-' }}</span>
              </div>

              <template v-if="member_profile.country === 'Ghana'">
                <div class="info-item">
                  <span class="info-label">Region</span>
                  <span class="info-value">{{ regionLabel || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Area</span>
                  <span class="info-value">{{ areaLabel || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">District</span>
                  <span class="info-value">{{ districtLabel || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Assembly</span>
                  <span class="info-value">{{ assemblyLabel || '-' }}</span>
                </div>
              </template>
              <template v-else>
                <div class="info-item">
                  <span class="info-label">Region</span>
                  <span class="info-value">{{ member_profile.region || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Area</span>
                  <span class="info-value">{{ member_profile.area || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">District</span>
                  <span class="info-value">{{ member_profile.district || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Assembly</span>
                  <span class="info-value">{{ member_profile.assembly || '-' }}</span>
                </div>
              </template>
            </div>

            <div class="info-card mt-4">
              <h5 class="card-header-title mb-4">
                <feather-icon icon="BookOpenIcon" size="18" />
                Role Information
              </h5>

              <div class="info-item">
                <span class="info-label">Member Type</span>
                <span class="info-value">{{ memberTypeLabel || '-' }}</span>
              </div>

              <div class="info-item">
                <span class="info-label">Member Title</span>
                <span class="info-value">{{ memberTitleLabel || '-' }}</span>
              </div>

              <div class="info-item">
                <span class="info-label">Member Designation</span>
                <span class="info-value">{{ memberDesignationLabel || '-' }}</span>
              </div>

              <div v-if="isCopStaff || isCopMinister" class="info-item">
                <span class="info-label">Department</span>
                <span class="info-value">{{ departmentLabel || '-' }}</span>
              </div>

              <div v-if="isCopMinister" class="info-item">
                <span class="info-label">Minister Id</span>
                <span class="info-value">{{ member_profile.minister_id || '-' }}</span>
              </div>

              <div v-if="isCopMinister" class="info-item">
                <span class="info-label">Year Called Into Ministry</span>
                <span class="info-value">{{ member_profile.year_called_into_ministry || '-' }}</span>
              </div>

              <div class="info-item">
                <span class="info-label">Tent Minister</span>
                <span class="info-value">{{ member_profile.is_tent_minister ? 'Yes' : 'No' }}</span>
              </div>
            </div>
          </b-col>

          <!-- Main Content Tabs -->
          <b-col cols="12" lg="8">
            <b-tabs class="profile-tabs" pills>
              <b-tab active>
                <template #title>
                  <feather-icon icon="AwardIcon" size="16" />
                  <span>Education</span>
                </template>
                <div class="tab-card">
                  <div class="tab-header">
                    <h4 class="tab-title">Educational History</h4>
                  </div>
                  <div class="tab-content">
                    <EducationHistoryList
                      v-if="currentUser._id"
                      :is-personal="true"
                      :user-id="currentUser._id"
                    />
                  </div>
                </div>
              </b-tab>

              <b-tab>
                <template #title>
                  <feather-icon icon="BriefcaseIcon" size="16" />
                  <span>Work Experience</span>
                </template>
                <div class="tab-card">
                  <div class="tab-header">
                    <h4 class="tab-title">Work Experience</h4>
                  </div>
                  <div class="tab-content">
                    <WorkExperienceList
                      v-if="currentUser._id"
                      :is-personal="true"
                      :user-id="currentUser._id"
                    />
                  </div>
                </div>
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>
      </div>
    </div>
  </b-overlay>
</template>

<script>
import { BRow, BCol, BLink, BButton, BAvatar, BBadge, BTabs, BTab, BOverlay } from 'bootstrap-vue'
import { get, pick } from "lodash";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import EducationHistoryList from "@/@core/components/education/EducationHistoryList.vue";
import WorkExperienceList from "@/@core/components/work-experience/WorkExperienceList.vue";

const allowed_member_profile_fields = [
  "status",
  "member_type_id",
  "gender",
  "email",
  "member_title_id",
  "member_designation_id",
  "minister_id",
  "year_called_into_ministry",
  "country",
  "region",
  "area",
  "district",
  "assembly",
  "region_id",
  "area_id",
  "district_id",
  "assembly_id",
  "is_schooling",
  "institution",
  "schooling_start_date",
  "schooling_end_date",
  "is_tent_minister",
  "department_id",
  "date_of_birth",
];

export default {
  name: 'MemberProfileDetail',
  components: {
    BRow,
    BCol,
    BLink,
    BButton,
    BAvatar,
    BBadge,
    BTabs,
    BTab,
    BOverlay,
    EducationHistoryList,
    WorkExperienceList,
  },
  data() {
    return {
      loading: false,

      member_profile: {
        member_type_id: "",
        member_title_id: "",
        member_designation_id: "",

        minister_id: "",
        year_called_into_ministry: "",

        date_of_birth: "",
        gender: "",
        country: "Ghana",
        region: "",
        area: "",
        district: "",
        assembly: "",
        region_id: "",
        area_id: "",
        district_id: "",
        assembly_id: "",
        department_id: "",

        is_tent_minister: false,
      },

      genderOptions: [
        { value: "female", label: "Female" },
        { value: "male", label: "Male" },
      ],

      areaOptions: [],
      regionOptions: [],
      districtOptions: [],
      assemblyOptions: [],

      memberTypeOptions: [],
      memberTitleOptions: [],
      memberDesignationOptions: [],
      departmentOptions: [],
    }
  },
  computed: {
    fullName() {
      return this.getValueFromSource(this.currentUser, 'member_profile.full_name', '') ||
        `${this.getValueFromSource(this.currentUser, 'member_profile.first_name', '')} ${this.getValueFromSource(this.currentUser, 'member_profile.last_name', '')}`.trim();
    },
    avatarPath() {
      return this.getValueFromSource(this.currentUser, 'member_profile.avatar.path', '');
    },
    email() {
      return this.getValueFromSource(this.currentUser, 'email', '');
    },
    phone() {
      return this.getValueFromSource(this.currentUser, 'phone', '');
    },
    emailVerified() {
      return this.getValueFromSource(this.currentUser, 'meta.email_verified', false);
    },
    phoneVerified() {
      return this.getValueFromSource(this.currentUser, 'meta.phone_verified', false);
    },
    isCopMinister() {
      const target = this.memberTypeOptions.find((option) => option.value === this.member_profile.member_type_id);
      return target ? target.is_cop_minister : false;
    },
    isCopStaff() {
      const target = this.memberTypeOptions.find((option) => option.value === this.member_profile.member_type_id);
      return target ? target.is_cop_staff : false;
    },
    roleLine() {
      return [this.memberTitleLabel, this.memberDesignationLabel].filter(Boolean).join(' • ');
    },
    locationSummary() {
      if (this.member_profile.country === 'Ghana') {
        return [this.districtLabel, this.assemblyLabel].filter(Boolean).join(', ');
      }
      return [this.member_profile.district, this.member_profile.country].filter(Boolean).join(', ');
    },
    memberTypeLabel() {
      return this.optionLabel(this.memberTypeOptions, this.member_profile.member_type_id);
    },
    memberTitleLabel() {
      return this.optionLabel(this.memberTitleOptions, this.member_profile.member_title_id);
    },
    memberDesignationLabel() {
      return this.optionLabel(this.memberDesignationOptions, this.member_profile.member_designation_id);
    },
    departmentLabel() {
      return this.optionLabel(this.departmentOptions, this.member_profile.department_id);
    },
    genderLabel() {
      return this.optionLabel(this.genderOptions, this.member_profile.gender);
    },
    regionLabel() {
      return this.optionLabel(this.regionOptions, this.member_profile.region_id);
    },
    areaLabel() {
      return this.optionLabel(this.areaOptions, this.member_profile.area_id);
    },
    districtLabel() {
      return this.optionLabel(this.districtOptions, this.member_profile.district_id);
    },
    assemblyLabel() {
      return this.optionLabel(this.assemblyOptions, this.member_profile.assembly_id);
    },
  },
  watch: {
    "member_profile.region_id": {
      handler(changed) {
        if (changed && this.member_profile.country === "Ghana") {
          this.fetchChurchAreas();
        }
      },
      immediate: false,
    },
    "member_profile.area_id": {
      handler(changed) {
        if (changed && this.member_profile.country === "Ghana") {
          this.fetchChurchDistrict();
        }
      },
      immediate: false,
    },
    "member_profile.district_id": {
      handler(changed) {
        if (changed && this.member_profile.country === "Ghana") {
          this.fetchChurchAssemblies();
        }
      },
      immediate: false,
    },
    "member_profile.member_type_id": {
      handler(val) {
        val && this.fetchMemberTitlesAndDesignations();
      },
    },
  },
  created() {
    this.fetchChurchRegions();
    this.fetchMemberTypes();
    this.fetchDepartments();
    this.fetchProfile();
  },
  methods: {
    optionLabel(options, value) {
      const match = options.find((option) => option.value === value);
      return match ? match.label : "";
    },
    async fetchChurchRegions() {
      try {
        const request = await this.useJwt().sharedService.fetchGeoRegions();
        const { data } = request.data;
        this.regionOptions = data.map((opt) => ({ label: opt.title, value: opt._id }));
      } catch (error) {
        this.notifyError(error);
      }
    },
    async fetchChurchAreas() {
      try {
        const request = await this.useJwt().sharedService.fetchGeoAreas({ region_id: this.member_profile.region_id });
        const { data } = request.data;
        this.areaOptions = data.map((opt) => ({ label: opt.title, value: opt._id }));
      } catch (error) {
        this.notifyError(error);
      }
    },
    async fetchChurchDistrict() {
      try {
        const request = await this.useJwt().sharedService.fetchGeoDistricts({ area_id: this.member_profile.area_id });
        const { data } = request.data;
        this.districtOptions = data.map((opt) => ({ label: opt.title, value: opt._id }));
      } catch (error) {
        this.notifyError(error);
      }
    },
    async fetchChurchAssemblies() {
      try {
        const request = await this.useJwt().sharedService.fetchGeoAssemblies({ district_id: this.member_profile.district_id });
        const { data } = request.data;
        this.assemblyOptions = data.map((opt) => ({ label: opt.title, value: opt._id }));
      } catch (error) {
        this.notifyError(error);
      }
    },
    async fetchMemberTypes() {
      try {
        const response = await this.useJwt().sharedService.fetchMemberTypes();
        const member_types = get(response, "data");
        this.memberTypeOptions = member_types.map((member_type) => ({
          value: member_type._id,
          label: member_type.title,
          is_cop_minister: member_type.is_cop_minister,
          is_cop_staff: member_type.is_cop_staff,
        }));
      } catch (error) {
        this.notifyError(error);
      }
    },
    async fetchDepartments() {
      try {
        const response = await this.useJwt().sharedService.fetchDepartments();
        const departments = get(response, "data");
        this.departmentOptions = departments.map((department) => ({ value: department._id, label: department.title }));
      } catch (error) {
        this.notifyError(error);
      }
    },
    async fetchMemberTitlesAndDesignations() {
      try {
        const query = { member_type_id: this.member_profile.member_type_id };
        const member_titles_resp = await this.useJwt().sharedService.fetchMemberTitles(query);
        const member_designations_resp = await this.useJwt().sharedService.fetchMemberDesignations(query);

        const member_titles = get(member_titles_resp, "data", []);
        const member_designations = get(member_designations_resp, "data", []);

        this.memberTitleOptions = member_titles.map((member_type_title) => ({ value: member_type_title._id, label: member_type_title.title }));
        this.memberDesignationOptions = member_designations.map((member_designation) => ({ value: member_designation._id, label: member_designation.designation }));
      } catch (error) {
        this.notifyError(error);
      }
    },
    async fetchProfile() {
      try {
        this.loading = true;
        const response = await this.useJwt().mainService.auth.getProfile();
        const member_profile = this.getValueFromSource(response, "data.data");
        this.member_profile = { ...this.member_profile, ...pick(member_profile, allowed_member_profile_fields) };
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.loading = false;
      }
    },
    notifyError(error) {
      const error_message = get(error, "response.data.message") || error.message;
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error!',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
          text: error_message
        },
      });
    },
  },
}
</script>

<style lang="scss" scoped>
.self-profile-view {
  min-height: 100vh;
  background: #f8f9fa;

  .hero-header {
    position: relative;
    background: linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%);
    color: white;
    overflow: hidden;

    .hero-pattern {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      opacity: 0.1;
      background-image:
        repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px),
        repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px);
    }

    .container-fluid {
      position: relative;
      z-index: 1;
    }

    .avatar-wrapper {
      display: inline-block;

      .profile-avatar {
        border: 4px solid rgba(255, 255, 255, 0.3);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
      }
    }

    .profile-header-info {
      text-align: left;

      @media (max-width: 767px) {
        text-align: center;
      }
    }

    .profile-name {
      font-size: 2rem;
      font-weight: 700;
      margin: 0;
      color: white;

      @media (max-width: 767px) {
        font-size: 1.5rem;
      }
    }

    .profile-title {
      font-size: 1.1rem;
      opacity: 0.95;
      font-weight: 500;
    }

    .profile-meta {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 0.9rem;
      opacity: 0.9;

      @media (max-width: 767px) {
        justify-content: center;
      }

      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }

    .action-btn {
      padding: 10px 20px;
      font-weight: 500;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
  }

  .info-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    }

    .card-header-title {
      font-size: 1rem;
      font-weight: 600;
      color: #1e3a5f;
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 20px;
      padding-bottom: 12px;
      border-bottom: 2px solid #e5e7eb;
    }

    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: start;
      padding: 12px 0;
      border-bottom: 1px solid #f3f4f6;

      &:last-child {
        border-bottom: none;
      }

      .info-label {
        font-size: 0.875rem;
        color: #6b7280;
        font-weight: 500;
        min-width: 80px;
      }

      .info-value {
        font-size: 0.875rem;
        color: #1f2937;
        font-weight: 600;
        text-align: right;
        flex: 1;
      }
    }
  }

  .profile-tabs {
    ::v-deep .nav-pills {
      border-bottom: none;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: center;

      @media (min-width: 768px) {
        justify-content: flex-start;
      }

      .nav-item {
        .nav-link {
          background: white;
          color: #4b5563;
          border-radius: 8px;
          padding: 12px 20px;
          font-weight: 500;
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;

          &:hover {
            background: #f3f4f6;
            border-color: #d1d5db;
          }

          &.active {
            background: #1e3a5f;
            color: white;
            border-color: #1e3a5f;
            box-shadow: 0 4px 12px rgba(30, 58, 95, 0.2);
          }
        }
      }
    }

    ::v-deep .tab-content {
      margin-top: 24px;
    }
  }

  .tab-card {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

    .tab-header {
      background: linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%);
      color: white;
      padding: 20px 24px;

      .tab-title {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: white;
      }
    }

    .tab-content {
      padding: 24px;
    }
  }
}

.dark-layout {
  .self-profile-view {
    background: #1f2937;

    .info-card,
    .tab-card {
      background: #374151;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);

      .card-header-title {
        color: #e5e7eb;
        border-bottom-color: #4b5563;
      }

      .info-item {
        border-bottom-color: #4b5563;

        .info-label {
          color: #9ca3af;
        }

        .info-value {
          color: #f3f4f6;
        }
      }
    }

    .profile-tabs {
      ::v-deep .nav-pills .nav-item .nav-link {
        background: #374151;
        color: #d1d5db;
        border-color: #4b5563;

        &:hover {
          background: #4b5563;
        }

        &.active {
          background: #1e3a5f;
          color: white;
        }
      }
    }
  }
}
</style>
